import { get, set } from 'lodash';
import styled from '@emotion/styled';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import * as React from 'react';
import { memo, createContext, useContext, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { keyframes, CacheProvider } from '@emotion/react';
import { serializeStyles } from '@emotion/serialize';
import { insertStyles } from '@emotion/utils';
import createCache from '@emotion/cache';

const rangeStep = (start, step, stop, modifier) => {
    const result = [];
    if (start < stop) {
        for (let i = start; i <= stop; i += step) {
            result.push(modifier ? modifier(i) : i);
        }
    }
    else {
        for (let i = stop; i >= start; i -= step) {
            result.push(modifier ? modifier(i) : i);
        }
    }
    return result;
};

const toPx = (i) => i + 'px';
const toRem = (i) => i + 'rem';
const px = (values) => values.map(toPx);
const breakpoints = px([576, 768, 992, 1200, 1800]);
const staticValues = {
    media: breakpoints.map((b) => `@media (min-width: ${b})`),
    space: rangeStep(0, 0.25, 6, toRem),
    fontSizes: px([8, 10, 12, 14, 16, 20, 24, 36, 48, 54, 80, 96]),
    fontWeights: rangeStep(100, 100, 900),
    fixedFontWeights: {
        l: 300,
        r: 400,
        m: 500,
        b: 700,
    },
    lineHeights: {
        // todo use space instead
        solid: 1,
        title: 1.25,
        copy: 1.5,
    },
    letterSpacings: {
        normal: 'normal',
        tracked: '0.1em',
        tight: '-0.05em',
        mega: '0.25em',
    },
    fonts: {
        roboto_thin: "'RobotoThin'",
        roboto: "'Roboto', sans-serif",
        roboto_condensed_regular: "'RobotoCondensed Regular', sans-serif",
        roboto_mono: "'RobotoMono Regular', sans-serif",
    },
    borders: [
        '1px solid',
        '2px solid',
        '4px solid',
        '8px solid',
        '16px solid',
        '32px solid',
    ],
    radii: [0, '2px', '4px', '6px', '16px', '100%'],
    width: [16, 32, 64, 128, 256],
    heights: [16, 32, 64, 128, 256],
    maxWidths: [16, 32, 64, 128, 256, 512, 768, 1024, 1536],
    minWidths: [],
    maxHeights: [],
    minHeights: [],
};

const interpolateArg = (x, path, props) => get(props, ['theme', ...path, x]);
const interpolateArgs = (xs, path, props) => xs.map((x) => interpolateArg(x, path, props));
const interpolateNegativeArgs = (xs, path, props) => xs.map((x) => {
    let t = typeof x === 'number' ? Math.abs(x) : x;
    let sign;
    if (typeof x === 'number') {
        t = Math.abs(x);
        sign = t !== x ? '-' : void 0;
    }
    const value = interpolateArg(t, path, props);
    return sign ? sign + value : value;
});
function createHelper(prop, path, interpolateFn = interpolateArgs, join = ' ') {
    const cache = {};
    const flatCache = {};
    return (t1, t2, t3, t4) => {
        var _a, _b, _c;
        if (t2 == null) {
            let value = flatCache[t1];
            if (!value) {
                value = {
                    [prop]: interpolateFn([t1], path, { theme: staticValues }).join(join),
                };
                flatCache[t1] = value;
            }
            return value;
        }
        let _t2;
        let _t3;
        let _t4;
        if (typeof t1 === 'number') {
            _t2 = t2 !== null && t2 !== void 0 ? t2 : Number.MAX_SAFE_INTEGER;
            _t3 = t3 !== null && t3 !== void 0 ? t3 : Number.MAX_SAFE_INTEGER;
            _t4 = t4 !== null && t4 !== void 0 ? t4 : Number.MAX_SAFE_INTEGER;
        }
        else {
            _t2 = t2 !== null && t2 !== void 0 ? t2 : '.';
            _t3 = t3 !== null && t3 !== void 0 ? t3 : '.';
            _t4 = t4 !== null && t4 !== void 0 ? t4 : '.';
        }
        let value = (_c = (_b = (_a = cache[t1]) === null || _a === void 0 ? void 0 : _a[_t2]) === null || _b === void 0 ? void 0 : _b[_t3]) === null || _c === void 0 ? void 0 : _c[_t4];
        if (!value) {
            const xs = [t1, _t2, _t3, _t4];
            value = {
                [prop]: interpolateFn(xs, path, { theme: staticValues }).join(join),
            };
            set(cache, xs, value);
        }
        return value;
    };
}
const fontSize = createHelper('fontSize', ['fontSizes']);
const fontFamily = createHelper('fontFamily', [
    'fonts',
]);
const fontWeight = createHelper('fontWeight', [
    'fixedFontWeights',
]);
const paddingLeft = createHelper('paddingLeft', ['space']);
const paddingRight = createHelper('paddingRight', ['space']);
const paddingTop = createHelper('paddingTop', ['space']);
const paddingBottom = createHelper('paddingBottom', ['space']);
const padding = createHelper('padding', [
    'space',
]);
const margin = createHelper('margin', ['space'], interpolateNegativeArgs);
const marginTop = createHelper('marginTop', ['space'], interpolateNegativeArgs);
const marginBottom = createHelper('marginBottom', ['space'], interpolateNegativeArgs);
const marginRight = createHelper('marginRight', ['space'], interpolateNegativeArgs);
const marginLeft = createHelper('marginLeft', ['space'], interpolateNegativeArgs);
const borderRadius = createHelper('borderRadius', ['radii']);
const borderTopLeftRadius = createHelper('borderTopLeftRadius', [
    'radii',
]);
const borderTopRightRadius = createHelper('borderTopRightRadius', ['radii']);
const borderBottomLeftRadius = createHelper('borderBottomLeftRadius', ['radii']);
const borderBottomRightRadius = createHelper('borderBottomRightRadius', ['radii']);
const top = createHelper('top', ['space'], interpolateNegativeArgs);
const bottom = createHelper('bottom', ['space'], interpolateNegativeArgs);
const right = createHelper('right', ['space'], interpolateNegativeArgs);
const left = createHelper('left', ['space'], interpolateNegativeArgs);
const minWidth = createHelper('minWidth', ['space']);
const minHeight = createHelper('minHeight', ['space']);
const height = createHelper('height', ['space']);
const width = createHelper('width', ['space']);
const maxWidth = createHelper('maxWidth', ['space']);
const maxHeight = createHelper('maxHeight', ['space']);
// todo use lineHeights ?!
const lineHeight = createHelper('lineHeight', ['space']);
const gridGap = createHelper('gridGap', ['space']);
const gridRowGap = createHelper('gridRowGap', ['space']);
const gridColumnGap = createHelper('gridColumnGap', ['space']);
const gap = createHelper('gap', ['space']);
const EMPTY = {};
function getTheme(...path) {
    const propPath = [
        'theme',
        'components',
        ...(Array.isArray(path[0]) ? path[0] : path),
    ];
    return (props) => {
        const styles = get(props, propPath);
        if (undefined === styles) {
            // if (process.env.NODE_ENV === 'development') {
            //   namespace('app').warn(`styles for path "${path.join('.')}" not found.`);
            // }
            return EMPTY;
        }
        return styles;
    };
}
const hover = (fn) => (props) => ({ '&:hover': fn(props) });

const colors2 = {
    color000: '#000000',
    color00bb5b: '#00bb5b',
    color08c156: '#08c156',
    color0970bc: '#0970bc',
    color0f3040: '#0f3040',
    color111521: '#111521',
    color142537: '#142537',
    color18d235: '#18d235',
    color19202e: '#19202e',
    color1ddd67: '#1ddd67',
    color20bb3c: '#20bb3c',
    color222938: '#222938',
    color22d93f: '#22d93f',
    color2586f6: '#2586f6',
    color27384d: '#27384d',
    color283142: '#283142',
    color2c3b4d: '#2c3b4d',
    color314d76: '#314d76',
    color346eb5: '#346eb5',
    color363d4f: '#363d4f',
    color3983e1: '#3983e1',
    color414b5e: '#414b5e',
    color444444: '#444444',
    color476a94: '#476a94',
    color497eb6: '#497eb6',
    color4ac8ff: '#4ac8ff',
    color4cd662: '#4cd662',
    color4d90f0: '#4d90f0',
    color4e5a73: '#4e5a73',
    color4e95ee: '#4e95ee',
    color4e9dff: '#4e9dff',
    color5076a6: '#5076a6',
    color51ef41: '#51ef41',
    color525359: '#525359',
    color5288d3: '#5288d3',
    color54abf0: '#54abf0',
    color586784: '#586784',
    color5d6f92: '#5d6f92',
    color5c6168: '#5c6168',
    color5e6c80: '#5e6c80',
    color6092d9: '#6092d9',
    color616975: '#616975',
    color68582b: '#68582b',
    color70727b: '#70727b',
    color71a3e2: '#71a3e2',
    color7282a0: '#7282a0',
    color77a6df: '#77a6df',
    color78fa6a: '#78fa6a',
    color7b7f91: '#7b7f91',
    color7d7d7d: '#7d7d7d',
    color7dabed: '#7dabed',
    color85bff6: '#85bff6',
    color898b93: '#898b93',
    color8997b2: '#8997b2',
    color8b8fa4: '#8b8fa4',
    color91a4c8: '#91a4c8',
    color999daf: '#999daf',
    color9bc8ff: '#9bc8ff',
    colora0a4b2: '#a0a4b2',
    colora2a7c8: '#a2a7c8',
    coloraabed1: '#aabed1',
    coloracb0c2: '#acb0c2',
    coloracceff: '#acceff',
    coloraeaf0a: '#aeaf0a',
    colorb7bec5: '#b7bec5',
    colorbaac16: '#baac16',
    colorc5c5c5: '#c5c5c5',
    colorcfe7f6: '#cfe7f6',
    colord4a065: '#d4a065',
    colord5d8e1: '#d5d8e1',
    colordae3ef: '#dae3ef',
    colordbeaff: '#dbeaff',
    colordc264f: '#dc264f',
    colore4eaf3: '#e4eaf3',
    colorf02c55: '#f02c55',
    colorf48337: '#f48337',
    colorf4f7fb: '#f4f7fb',
    colorf89219: '#f89219',
    colorf9b565: '#f9b565',
    colorfa6a96: '#fa6a96',
    colorfc0b41: '#fc0b41',
    colorfcfcfc: '#fcfcfc',
    colorff2566: '#ff2566',
    colorffcd22: '#ffcd22',
    colorffe64a: '#ffe64a',
    colorfff1f4: '#fff1f4',
    colorffffff: '#ffffff',
    colord5ad46: '#D5AD46',
    color627eea: '#627EEA',
    color26a17b: '#26A17B',
    colord95abd: '#D95ABD',
};

const AppContainerStyles = {
    dark: {
        appContainer: {
            color: colors2.colorfcfcfc,
            background: colors2.color19202e,
        },
    },
    light: {
        appContainer: {
            color: colors2.color000,
            background: colors2.colorfcfcfc,
        },
    },
};

const hexToRgb = (hex) => {
    // http://stackoverflow.com/a/5624139
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
};
const rgba = (hex, alpha) => {
    const color = hexToRgb(hex);
    return !color ? '' : `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
};

const disabledDark = {
    color: colors2.color5c6168,
    boxShadow: `inset 0 0 6px ${rgba(colors2.color000, 0.32)}`,
    background: rgba(colors2.color000, 0.32),
    border: `1px solid ${rgba(colors2.color000, 0.28)}`,
};
const disabledLight = {
    color: colors2.colorfcfcfc,
    boxShadow: `0 0 2px ${colors2.coloraabed1}`,
    background: colors2.colordae3ef,
    border: `1px solid ${colors2.colord5d8e1}`,
};
const ButtonStyles = {
    dark: {
        button: {
            default: {
                base: {
                    color: colors2.colorfcfcfc,
                    border: '1px solid transparent',
                },
            },
            close: {
                base: {
                    color: colors2.color7282a0,
                },
                hover: {
                    color: colors2.colorfcfcfc,
                },
            },
            blue: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.color346eb5,
                    boxShadow: `0 0 3px 1px ${rgba(colors2.color4ac8ff, 0.28)}`,
                },
                hover: {
                    background: colors2.color0970bc,
                    boxShadow: `0 0 3px 1px ${rgba(colors2.color2586f6, 0.28)}`,
                },
                disabled: disabledDark,
            },
            green: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.color08c156,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.color78fa6a, 0.5)}`,
                },
                hover: {
                    background: colors2.color1ddd67,
                    boxShadow: `0 0 4px 1px ${colors2.color00bb5b}`,
                },
                disabled: disabledDark,
            },
            red: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.colordc264f,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.colorfa6a96, 0.5)}`,
                },
                hover: {
                    background: colors2.colorfc0b41,
                    boxShadow: `0 0 4px 1px ${colors2.colorf02c55}`,
                },
                disabled: disabledDark,
            },
            yellow: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.colorbaac16,
                    boxShadow: `0 0 3px 1px ${rgba(colors2.colorffe64a, 0.28)}, inset 0 0 10px 3px ${rgba(colors2.coloraeaf0a, 0.5)}`,
                },
                hover: {
                    background: colors2.colorfc0b41,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.colorfa6a96, 0.5)}`,
                },
                disabled: disabledDark,
            },
            securityFilter: {
                button: {
                    base: {
                        background: colors2.color19202e,
                        border: `1px solid ${colors2.color363d4f}`,
                        color: colors2.colorfcfcfc,
                    },
                    hover: {
                        color: colors2.colorfcfcfc,
                        background: colors2.color4e5a73,
                    },
                    active: {
                        background: colors2.color363d4f,
                    },
                },
                icon: {
                    base: {
                        fill: colors2.colorfcfcfc,
                    },
                    hover: {
                        fill: colors2.colorfcfcfc,
                    },
                },
            },
            white: {
                base: {
                    background: 'transparent',
                    color: colors2.color7282a0,
                },
                hover: {
                    color: colors2.colorfcfcfc,
                },
            },
            favorite: {
                active: {
                    color: colors2.colorffcd22,
                },
            },
        },
    },
    light: {
        button: {
            default: {
                base: {
                    color: colors2.color000,
                },
            },
            close: {
                base: {
                    color: colors2.coloracb0c2,
                },
                hover: {
                    color: colors2.color70727b,
                },
            },
            blue: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.color7dabed,
                    boxShadow: `0 0 2px 1px ${colors2.color85bff6}`,
                },
                hover: {
                    background: colors2.color4d90f0,
                    boxShadow: `0 0 2px 0 ${rgba(colors2.color2586f6, 0.28)}, inset 0 0 12px transparent`,
                },
                disabled: disabledLight,
            },
            green: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.color4cd662,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.color78fa6a, 0.5)}`,
                },
                hover: {
                    background: colors2.color22d93f,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.color51ef41, 0.5)}, inset 0 0 6px ${colors2.color18d235}`,
                },
                disabled: disabledLight,
            },
            red: {
                base: {
                    color: colors2.colorfcfcfc,
                    background: colors2.colorf02c55,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.colorfa6a96, 0.5)}`,
                },
                hover: {
                    background: colors2.colorfc0b41,
                    boxShadow: `0 0 4px 1px ${rgba(colors2.colorfa6a96, 0.5)}`,
                },
                disabled: disabledLight,
            },
            yellow: {
                disabled: disabledLight,
            },
            securityFilter: {
                button: {
                    base: {
                        background: colors2.coloracb0c2,
                        border: `1px solid ${colors2.colorf4f7fb}`,
                        color: colors2.colorfcfcfc,
                    },
                    hover: {
                        color: colors2.colorfcfcfc,
                        background: colors2.color999daf,
                    },
                    active: {
                        background: colors2.color8b8fa4,
                    },
                },
                icon: {
                    base: {
                        fill: colors2.colorfcfcfc,
                    },
                    hover: {
                        fill: colors2.colorfcfcfc,
                    },
                },
            },
            white: {
                base: {
                    color: colors2.coloracb0c2,
                },
                hover: {
                    color: colors2.color70727b,
                },
            },
            favorite: {
                active: {
                    color: colors2.colorffcd22,
                },
            },
        },
    },
};

const CheckboxStyles = {
    dark: {
        checkbox: {
            base: {
                boxShadow: `0 0 4px 0 ${colors2.color4e9dff}`,
                background: colors2.color346eb5,
                border: `1px solid ${colors2.color346eb5}`,
            },
            invalid: {
                border: `1px solid ${colors2.colorff2566}`,
            },
            disabled: {
                boxShadow: 'none',
            },
        },
    },
    light: {
        checkbox: {
            base: {
                boxShadow: `0 0 4px 0 ${colors2.color85bff6}`,
                background: colors2.color7dabed,
                border: `1px solid ${colors2.color7dabed}`,
            },
            invalid: {
                border: `1px solid ${colors2.colorff2566}`,
            },
            disabled: {
                boxShadow: 'none',
            },
        },
    },
};

const ColoredTextStyles = {
    dark: {
        coloredText: {
            red: {
                color: colors2.colorff2566,
            },
            green: {
                color: colors2.color1ddd67,
            },
        },
    },
    light: {
        coloredText: {
            red: {
                color: colors2.colorf02c55,
            },
            green: {
                color: colors2.color20bb3c,
            },
        },
    },
};

const ContentBlockStyles = {
    dark: {
        contentBlock: {
            background: colors2.color27384d,
            boxShadow: `0 0 6px ${rgba(colors2.color111521, 0.47)}`,
            color: colors2.colorfcfcfc, // todo remove
        },
    },
    light: {
        contentBlock: {
            background: colors2.colorf4f7fb,
            boxShadow: `0 0 2px ${rgba(colors2.color000, 0.39)}`,
            color: colors2.color000, // todo remove
        },
    },
};

const HeaderButtonStyles = {
    dark: {
        headerButton: {
            base: {
                color: colors2.color7282a0,
                fill: colors2.color7282a0,
                background: colors2.color283142,
                boxShadow: `0 0 2px ${colors2.color71a3e2}`,
            },
            hover: {
                background: colors2.color2c3b4d,
                boxShadow: `0 0 2px ${rgba(colors2.colorfcfcfc, 0.77)}`,
            },
        },
    },
    light: {
        headerButton: {
            base: {
                color: colors2.coloracb0c2,
                fill: colors2.coloracb0c2,
                background: colors2.colorfcfcfc,
                boxShadow: `0 0 2px ${colors2.colora2a7c8}`,
            },
            hover: {
                boxShadow: `0 0 2px ${colors2.color7dabed}`,
                color: colors2.color7dabed,
                fill: colors2.color7dabed,
                background: colors2.colorfcfcfc,
            },
        },
    },
};

const InputStyles = {
    dark: {
        input: {
            base: {
                color: colors2.colorfcfcfc,
                background: 'transparent',
            },
            invalid: {
                boxShadow: `0 0 2px 0 ${colors2.colorff2566}`,
                border: `1px solid ${colors2.colorff2566}`,
            },
            close: {
                base: {
                    background: colors2.color586784,
                    color: colors2.colorffffff,
                },
                hover: {
                    background: colors2.color3983e1,
                },
            },
        },
    },
    light: {
        input: {
            base: {
                color: colors2.color5e6c80,
            },
        },
    },
};
const InputSearchStyles = {
    dark: {
        inputSearch: {
            base: Object.assign(Object.assign({}, InputStyles.dark.input.base), { border: `1px solid ${colors2.color363d4f}` }),
            fromTo: Object.assign(Object.assign({}, InputStyles.dark.input.base), { border: `1px solid ${colors2.color586784}` }),
            invalid: Object.assign({}, InputStyles.dark.input.invalid),
            icon: {},
        },
    },
    light: {
        inputSearch: {
            base: Object.assign(Object.assign({}, InputStyles.light.input.base), { border: `1px solid ${colors2.colord5d8e1}` }),
            fromTo: Object.assign(Object.assign({}, InputStyles.light.input.base), { border: `1px solid ${colors2.color586784}` }),
            invalid: Object.assign({}, InputStyles.dark.input.invalid),
            // todo input: {base, invalid}, icon: {}
            icon: {
                color: colors2.colorc5c5c5,
            },
        },
    },
};

const LabelStyles = {
    dark: {
        label: {
            color: colors2.colorc5c5c5,
        },
    },
    light: {
        label: {
            color: colors2.color616975,
        },
    },
};

const OuterCloseButtonStyles = {
    dark: {
        outerCloseButton: {
            base: {
                border: colors2.colorfcfcfc,
                background: rgba(colors2.color0f3040, 0.7),
                boxShadow: `0 0 4px ${rgba(colors2.colorfcfcfc, 0.5)}`,
                color: colors2.colorfcfcfc,
            },
            hover: {
                background: rgba(colors2.color2c3b4d, 0.7),
            },
        },
    },
    light: {
        outerCloseButton: {
            base: {
                border: undefined,
                background: colors2.colorfcfcfc,
                boxShadow: `0 0 4px ${colors2.coloracceff}`,
                color: colors2.color77a6df,
            },
            hover: {
                background: rgba(colors2.colorfcfcfc, 0.9),
            },
        },
    },
};

const OverlayStyles = {
    dark: {
        overlay: {
            wrapper: {
                background: rgba(colors2.color000, 0.3),
            },
            element: {
                background: colors2.colorfcfcfc,
            },
        },
    },
    light: {
        overlay: {
            wrapper: {
                background: rgba(colors2.colorfcfcfc, 0.3),
            },
            element: {
                background: colors2.color000,
            },
        },
    },
};

const ProgressStyles = {
    dark: {
        progress: {
            default: {
                base: {
                    border: '1px solid',
                },
                inner: {},
            },
            red: {
                base: {
                    border: `1px solid ${colors2.colorff2566}`,
                },
                inner: {
                    background: colors2.colorff2566,
                },
            },
            green: {
                base: {
                    border: `1px solid ${colors2.color1ddd67}`,
                },
                inner: {
                    background: colors2.color1ddd67,
                },
            },
        },
    },
    light: {
        progress: {
            red: {
                base: {
                    border: `1px solid ${colors2.colorf02c55}`,
                },
                inner: {
                    background: colors2.colorf02c55,
                },
            },
            green: {
                base: {
                    border: `1px solid ${colors2.color20bb3c}`,
                },
                inner: {
                    background: colors2.color20bb3c,
                },
            },
        },
    },
};

const RadioButtonStyles = {
    dark: {
        radioButton: {
            base: {
                borderBottom: `2px solid ${colors2.color476a94}`,
            },
            active: {
                borderBottom: `2px solid ${colors2.color77a6df}`,
            },
        },
    },
    light: {
        radioButton: {
            base: {
                borderBottom: `2px solid ${colors2.colord5d8e1}`,
            },
            active: {
                borderBottom: `2px solid ${colors2.color7dabed}`,
            },
        },
    },
};

const components = [
    AppContainerStyles,
    ButtonStyles,
    CheckboxStyles,
    ColoredTextStyles,
    ContentBlockStyles,
    LabelStyles,
    HeaderButtonStyles,
    OuterCloseButtonStyles,
    OverlayStyles,
    ProgressStyles,
    RadioButtonStyles,
    InputStyles,
    InputSearchStyles,
];
const baseTheme = {};

const AppContainer = styled('div')({
    height: '100%',
    overflow: 'hidden',
    userSelect: 'none',
}, getTheme(['appContainer']));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const buttonStylesMixin = (comp) => styled(comp)({
    whiteSpace: 'nowrap',
    outline: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:disabled': {
        cursor: 'default',
    },
}, borderRadius(2), padding(1, 2), fontSize(3), fontWeight('m'), getTheme(['button', 'default', 'base']));
const ButtonWrapper$1 = buttonStylesMixin('button');
const ButtonRaw = (_a, ref) => {
    var { type = 'button', children } = _a, props = __rest(_a, ["type", "children"]);
    return (jsx(ButtonWrapper$1, Object.assign({}, props, { ref: ref, type: type }, { children: jsx("div", { children: children }) })));
};
const Button = React.forwardRef(ButtonRaw);

const BlueButton = styled(Button)(getTheme(['button', 'blue', 'base']), (props) => ({
    '&:hover': [getTheme(['button', 'blue', 'hover'])(props)],
    '&:disabled': [getTheme(['button', 'blue', 'disabled'])(props)],
}));

const CrossSvg = () => {
    return (jsx("svg", Object.assign({ width: '0.6875em', height: '0.6875em', viewBox: '0 0 10 10', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, { children: jsx("path", { d: 'M6.89545 5L9.73835 2.1571C10.0872 1.80824 10.0872 1.24261 9.73835 0.893466L9.10653 0.261648C8.75767 -0.0872159 8.19205 -0.0872159 7.8429 0.261648L5 3.10455L2.1571 0.261648C1.80824 -0.0872159 1.24261 -0.0872159 0.893466 0.261648L0.261648 0.893466C-0.0872159 1.24233 -0.0872159 1.80795 0.261648 2.1571L3.10455 5L0.261648 7.8429C-0.0872159 8.19176 -0.0872159 8.75739 0.261648 9.10653L0.893466 9.73835C1.24233 10.0872 1.80824 10.0872 2.1571 9.73835L5 6.89545L7.8429 9.73835C8.19176 10.0872 8.75767 10.0872 9.10653 9.73835L9.73835 9.10653C10.0872 8.75767 10.0872 8.19205 9.73835 7.8429L6.89545 5Z', fill: 'currentColor' }) })));
};

const ButtonWrapper = styled('div')({
    border: 0,
    cursor: 'pointer',
    display: 'inline-block',
    pointerEvents: 'all',
}, fontSize(1), padding(1, 1), getTheme(['button', 'close', 'base']), (props) => ({
    '&:hover': {
        color: [getTheme(['button', 'close', 'hover'])(props)],
    },
}));
const CloseButtonRaw = (_a) => {
    var { onClick } = _a, props = __rest(_a, ["onClick"]);
    const click = React.useCallback((e) => {
        e.stopPropagation();
        onClick(e);
    }, [onClick]);
    return (jsx(ButtonWrapper, Object.assign({ onClick: click }, props, { children: jsx(CrossSvg, {}) })));
};
const CloseButton = React.memo(CloseButtonRaw);

const OnClickStopPropagation = (Component) => {
    return (_a) => {
        var { onClick } = _a, props = __rest(_a, ["onClick"]);
        const onClickStopPropagation = (e) => {
            e.stopPropagation();
            if (onClick) {
                onClick.call(null, e);
            }
        };
        return jsx(Component, Object.assign({}, props, { onClick: onClickStopPropagation }));
    };
};
const StopPropagation = (_a) => {
    var { onClick, children } = _a, props = __rest(_a, ["onClick", "children"]);
    const click = React.useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onClick) {
            onClick.call(null, e);
        }
    }, [onClick]);
    return (jsx("div", Object.assign({}, props, { onClick: click }, { children: children })));
};

const WhiteButton = styled(Button)(getTheme(['button', 'white', 'base']), (props) => ({
    '&:hover': [getTheme(['button', 'white', 'hover'])(props)],
}));

const ButtonWrapped = styled(WhiteButton)((props) => props.active && {
    '&:not(:hover)': [getTheme(['button', 'favorite', 'active'])(props)],
}, (props) => props.disabled &&
    props.active && {
    '&:hover': [getTheme(['button', 'favorite', 'active'])(props)],
});
const FavoriteButton = OnClickStopPropagation((_a) => {
    var { isFavorite, showTrashForRemove } = _a, props = __rest(_a, ["isFavorite", "showTrashForRemove"]);
    return (jsx(ButtonWrapped, Object.assign({}, props, { active: isFavorite && !showTrashForRemove }, { children: jsx(FontAwesomeIcon, { icon: isFavorite
                ? showTrashForRemove
                    ? 'trash'
                    : ['fas', 'star']
                : ['far', 'star'] }) })));
});

const GreenButton = styled(Button)(getTheme(['button', 'green', 'base']), (props) => ({
    '&:hover': [getTheme(['button', 'green', 'hover'])(props)],
    '&:disabled': [getTheme(['button', 'green', 'disabled'])(props)],
}));

const RedButton = styled(Button)(getTheme(['button', 'red', 'base']), (props) => ({
    '&:hover': [getTheme(['button', 'red', 'hover'])(props)],
    '&:disabled': [getTheme(['button', 'red', 'disabled'])(props)],
}));

const SecurityFilterButton = styled(buttonStylesMixin('div'))(padding(3, 3), borderRadius(1), getTheme(['button', 'securityFilter', 'button', 'base']), (props) => ({
    svg: [getTheme(['button', 'securityFilter', 'icon', 'base'])(props)],
    '&:hover': [
        getTheme(['button', 'securityFilter', 'button', 'hover'])(props),
        {
            svg: [getTheme(['button', 'securityFilter', 'icon', 'hover'])(props)],
        },
    ],
}), (props) => props.active && Object.assign(Object.assign({}, getTheme(['button', 'securityFilter', 'button', 'active'])(props)), { svg: [getTheme(['button', 'securityFilter', 'icon', 'active'])(props)] }));

const TransparentButton = styled(Button)({
    backgroundColor: 'transparent',
    boxShadow: 'none',
});

const YellowButton = styled(Button)(getTheme(['button', 'yellow', 'base']), (props) => ({
    '&:hover': [getTheme(['button', 'yellow', 'hover'])(props)],
    '&:disabled': [getTheme(['button', 'yellow', 'disabled'])(props)],
}));

const Label$2 = styled('label')(({ theme: { components: { checkbox }, radii, }, }) => {
    var _a, _b, _c, _d, _e;
    return ({
        position: 'relative',
        display: 'block',
        cursor: 'pointer',
        fontSize: 0,
        '&>input': {
            visibility: 'hidden',
            opacity: 0,
            padding: 0,
            margin: 0,
            '&:checked': {
                '~div': {
                    boxShadow: (_a = checkbox === null || checkbox === void 0 ? void 0 : checkbox.base) === null || _a === void 0 ? void 0 : _a.boxShadow,
                    '&:after': {
                        background: (_b = checkbox === null || checkbox === void 0 ? void 0 : checkbox.base) === null || _b === void 0 ? void 0 : _b.background,
                        content: "''",
                        margin: '2px',
                    },
                },
            },
            '&:disabled': {
                '~div': {
                    boxShadow: (_c = checkbox === null || checkbox === void 0 ? void 0 : checkbox.disabled) === null || _c === void 0 ? void 0 : _c.boxShadow,
                },
            },
        },
        '&>div': {
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            borderRadius: radii[1],
            border: (_d = checkbox === null || checkbox === void 0 ? void 0 : checkbox.base) === null || _d === void 0 ? void 0 : _d.border,
            '&:after': {
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
            },
        },
        '&.invalid': {
            '&>div': {
                border: (_e = checkbox === null || checkbox === void 0 ? void 0 : checkbox.invalid) === null || _e === void 0 ? void 0 : _e.border,
            },
        },
    });
});
const Input = styled('input')({
    cursor: 'pointer',
});
const Checkbox = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (jsxs(Label$2, Object.assign({ className: className }, { children: [jsx(Input, Object.assign({ type: 'checkbox' }, props)), jsx("div", {})] })));
};

const GreenText = styled('span')(getTheme(['coloredText', 'green']));

const RedText = styled('span')(getTheme(['coloredText', 'red']));

const ContentBlock = styled('div')(borderRadius(3), getTheme(['contentBlock']));

const Label$1 = styled('label')(fontWeight('l'), paddingTop(3), paddingBottom(1), fontSize(3), {
    width: '100%',
    display: 'block',
}, getTheme(['label']));

const HeaderButton = styled('div')({
    border: 0,
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    textAlign: 'center',
}, borderRadius(3), fontSize(4), getTheme(['headerButton', 'base']), (props) => ({
    '&:hover': [getTheme(['headerButton', 'hover'])(props)],
}));

const OuterCloseButtonWrapper = styled('div')({
    width: '17px',
    height: '17px',
    position: 'absolute',
    top: '-8.5px',
    right: '-8.5px',
    zIndex: 1,
    borderRadius: '100%',
    display: 'flex',
    alignItems: 'center',
}, (props) => ({
    '&:hover': [getTheme(['outerCloseButton', 'hover'])(props)],
}), getTheme(['outerCloseButton', 'base']));
const CenteredButton = styled(CloseButton)({
    textAlign: 'center',
    width: '100%',
    padding: 0,
});
const OuterCloseButton = (_a) => {
    var { onClick } = _a, props = __rest(_a, ["onClick"]);
    return (jsx(OuterCloseButtonWrapper, Object.assign({ onClick: onClick }, props, { children: jsx(CenteredButton, { onClick: onClick }) })));
};

const OverlayWrapper = styled('div')({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}, (props) => props.overlay !== false
    ? getTheme(['overlay', 'wrapper'])(props)
    : {
        background: 'transparent !important',
        position: 'relative',
        height: '100%',
    });
const SkFoldingCube = styled('div')({
    margin: '20px auto',
    width: '40px',
    height: '40px',
    position: 'relative',
    transform: 'rotateZ(45deg)',
});
const animation = keyframes({
    '0%, 10%': {
        transform: 'perspective(140px) rotateX(-180deg)',
        opacity: 0,
    },
    '25%, 75%': {
        transform: 'perspective(140px) rotateX(0deg)',
        opacity: 1,
    },
    '90%, 100%': {
        transform: 'perspective(140px) rotateY(180deg)',
        opacity: 0,
    },
});
const SkCube1 = styled('div')((props) => ({
    float: 'left',
    width: '50%',
    height: '50%',
    position: 'relative',
    transform: 'scale(1.1)',
    '&:before': Object.assign({ content: `''`, position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', animation: `${animation} 2.4s infinite linear both`, transformOrigin: '100% 100%' }, getTheme(['overlay', 'element'])(props)),
}));
const SkCube2 = styled(SkCube1)({
    transform: 'scale(1.1) rotateZ(90deg)',
    '&:before': {
        animationDelay: '0.3s',
    },
});
const SkCube3 = styled(SkCube1)({
    transform: 'scale(1.1) rotateZ(180deg)',
    '&:before': {
        animationDelay: '0.6s',
    },
});
const SkCube4 = styled(SkCube1)({
    transform: 'scale(1.1) rotateZ(270deg)',
    '&:before': {
        animationDelay: '0.9s',
    },
});
const Overlay = (_a) => {
    var { show } = _a, props = __rest(_a, ["show"]);
    return show ? (jsx(OverlayWrapper, Object.assign({}, props, { children: jsx("div", { children: jsxs(SkFoldingCube, { children: [jsx(SkCube1, {}), jsx(SkCube2, {}), jsx(SkCube4, {}), jsx(SkCube3, {})] }) }) }))) : null;
};

const TransparentOverlayRaw = styled(Overlay)({
    background: 'transparent',
});
const TransparentOverlay = memo(() => (jsx(TransparentOverlayRaw, { show: true })));

const ProgressOuter = styled('div')(getTheme(['progress', 'default', 'base']), borderRadius(4), {
    overflow: 'hidden',
    position: 'relative',
    height: '10px',
    width: '100%',
});
const ProgressInner = styled('div')({
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
}, ({ value }) => ({
    width: value == null ? void 0 : value + '%',
}));
const Progress = (_a) => {
    var { value } = _a, props = __rest(_a, ["value"]);
    return (jsx(ProgressOuter, Object.assign({}, props, { children: jsx(ProgressInner, { value: value }) })));
};

const GreenProgress = styled(Progress)(getTheme(['progress', 'green', 'base']), (props) => ({
    '>div': [getTheme(['progress', 'green', 'inner'])(props)],
}));

const RedProgress = styled(Progress)(getTheme(['progress', 'red', 'base']), (props) => ({
    '>div': [getTheme(['progress', 'red', 'inner'])(props)],
}));

const Underline = styled('div')({
    transition: 'border-color 300ms linear',
});
const Label = styled('label')({
    position: 'relative',
    display: 'block',
}, ({ theme: { components: { radioButton: { base, active }, }, }, }) => ({
    '& > input': {
        display: 'none',
        '&:checked ~ div': active,
        '&:hover ~ div': active,
        '& + div': Object.assign({ position: 'absolute', bottom: 0, left: 0, right: 0 }, base),
    },
}));
const RadioButton = (_a) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (jsxs(Label, Object.assign({ className: className }, { children: [children, jsx("input", Object.assign({ type: 'radio' }, props)), jsx(Underline, {})] })));
};

const Flex = styled('div')({
    display: 'flex',
});

const Flex1Horizontal = styled('div')({
    flex: 1,
    minWidth: 0,
});

const Flex1Vertical = styled('div')({
    flex: 1,
    minHeight: 0,
});

const FlexColumns = styled(Flex)({
    flexDirection: 'column',
});

const FlexRows = styled(Flex)({
    flexDirection: 'row',
    alignItems: 'center',
});

const KEY = 'WRAP_EMOTION_CACHE';
const createEmotionCache = () => {
    const cache = createCache({
        nonce: __webpack_nonce__,
        key: 'cc',
    });
    cache.compat = true;
    return typeof globalThis === 'object' &&
        typeof globalThis[KEY] === 'function'
        ? globalThis[KEY](cache)
        : cache;
};

const context = createContext(void 0);
const cache = createEmotionCache();
const EmotionCacheProvider = ({ children }) => (jsx(CacheProvider, Object.assign({ value: cache }, { children: jsx(context.Provider, Object.assign({ value: cache }, { children: children })) })));
const useEmotionCache = () => useContext(context);
function useCssClassName() {
    const cache = useEmotionCache();
    return useCallback((...args) => {
        if (!cache) {
            if (process.env.NODE_ENV === 'production') {
                return 'emotion-cache-missing';
            }
            throw new Error('No emotion cache found!');
        }
        const serialized = serializeStyles(args, cache.registered);
        insertStyles(cache, serialized, false);
        return cache.key + '-' + serialized.name;
    }, [cache]);
}
const withCssClassName = (Comp) => (props) => {
    const css = useCssClassName();
    return jsx(Comp, Object.assign({}, props, { cssClassName: css }));
};

const TextInput = styled('input')(getTheme(['inputSearch', 'base']), fontSize(2), padding(1), borderRadius(2), {
    outline: 0,
    minHeight: '24px',
}, (props) => props.invalid && [getTheme(['inputSearch', 'invalid'])(props)], (props) => ({
    '&.invalid': [getTheme(['inputSearch', 'invalid'])(props)],
}));

const PasswordInput = (_a) => {
    var { name = 'password', id = 'password', autoComplete = 'off', type = 'password', required = true, maxLength = 64 } = _a, props = __rest(_a, ["name", "id", "autoComplete", "type", "required", "maxLength"]);
    return (jsx(TextInput, Object.assign({ name: name, id: id, autoComplete: autoComplete, type: type, required: required, maxLength: maxLength }, props)));
};

const Textarea = styled('textarea')(getTheme(['inputSearch', 'base']), fontSize(2), padding(1), borderRadius(2), {
    outline: 0,
    minHeight: '24px',
}, (props) => ({
    '&.invalid': [getTheme(['inputSearch', 'invalid'])(props)],
}));

const VerificationCodeInput = (_a) => {
    var { name = 'code', id = 'code', autoComplete = 'off', type = 'text', required = true, maxLength = 16 } = _a, props = __rest(_a, ["name", "id", "autoComplete", "type", "required", "maxLength"]);
    return (jsx(TextInput, Object.assign({ name: name, id: id, autoComplete: autoComplete, type: type, required: required, maxLength: maxLength }, props)));
};

var _path$v, _path2$6;
function _extends$H() { _extends$H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$H.apply(this, arguments); }
var SvgExternal = function SvgExternal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$H({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 14 14"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "m3.795 9.187 7.31-7.284-.994-1c-.283-.285-.19-.921.397-.903l3.02.01c.336.007.443.113.45.453L14 3.513c.011.505-.564.736-.848.45l-.985-.991-7.31 7.284c-.145.146-.375.153-.463.064l-.664-.668c-.088-.088-.08-.318.065-.465Z"
  })), _path2$6 || (_path2$6 = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M11.837 7.455h.456a.454.454 0 0 1 .455.458v4.713c0 .364-.144.714-.4.971a1.361 1.361 0 0 1-.966.403H1.366c-.362 0-.71-.145-.966-.403a1.379 1.379 0 0 1-.4-.971V2.546c0-.364.144-.714.4-.971.256-.258.604-.403.966-.403h5.593a.454.454 0 0 1 .456.458v.458a.46.46 0 0 1-.456.459H1.366v10.079h10.016V7.913a.46.46 0 0 1 .455-.458Z"
  })));
};

var _path$u;
function _extends$G() { _extends$G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$G.apply(this, arguments); }
var SvgCopy = function SvgCopy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$G({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 14 14"
  }, props), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.688 0h-8.75c-.725 0-1.313.588-1.313 1.313v1.312H1.312C.589 2.625 0 3.213 0 3.938v8.75C0 13.412.588 14 1.313 14h8.75c.724 0 1.312-.588 1.312-1.313v-1.312h1.313c.724 0 1.312-.588 1.312-1.313v-8.75C14 .589 13.412 0 12.687 0Zm-2.79 12.688H1.477a.164.164 0 0 1-.165-.165V4.102a.164.164 0 0 1 .165-.165h1.148v6.126c0 .724.588 1.312 1.313 1.312h6.124v1.148a.164.164 0 0 1-.164.165Zm2.625-2.626H4.102a.164.164 0 0 1-.165-.164V1.477a.164.164 0 0 1 .165-.165h8.421a.164.164 0 0 1 .165.165v8.421a.164.164 0 0 1-.165.165Z"
  })));
};

var _path$t, _path2$5;
function _extends$F() { _extends$F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$F.apply(this, arguments); }
var SvgChartTv = function SvgChartTv(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$F({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 14 14"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.688.875H1.312C.589.875 0 1.463 0 2.188v9.624c0 .725.588 1.313 1.313 1.313h11.374c.725 0 1.313-.588 1.313-1.313V2.188c0-.725-.588-1.313-1.313-1.313Zm0 10.773c0 .09-.074.165-.165.165H1.477a.165.165 0 0 1-.165-.165V3.172h11.376v8.476Z"
  })), _path2$5 || (_path2$5 = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.655 10.511h.872c.146 0 .291-.15.291-.3v-3.15c0-.15-.145-.3-.29-.3h-.873c-.146 0-.291.15-.291.3v3.15c0 .15.145.3.29.3Zm2.181 0h.873c.146 0 .291-.15.291-.3v-5.4c0-.15-.146-.3-.29-.3h-.874c-.145 0-.29.15-.29.3v5.4c0 .15.145.3.29.3Zm-6.545 0h.873c.145 0 .29-.15.29-.3v-1.65c0-.15-.145-.3-.29-.3H3.29c-.146 0-.291.15-.291.3v1.65c0 .15.145.3.29.3Zm2.182 0h.872c.146 0 .291-.15.291-.3v-4.65c0-.15-.145-.3-.29-.3h-.873c-.146 0-.291.15-.291.3v4.65c0 .15.145.3.29.3Z"
  })));
};

var _path$s;
function _extends$E() { _extends$E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$E.apply(this, arguments); }
var SvgFloppyDisk = function SvgFloppyDisk(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$E({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 12 12"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.686 3.186 8.814 1.314C8.613 1.113 8.34 1 8.056 1H2.071C1.48 1 1 1.48 1 2.071V9.93C1 10.52 1.48 11 2.071 11H9.93C10.52 11 11 10.52 11 9.929V3.944c0-.284-.113-.557-.314-.758ZM6 9.571a1.429 1.429 0 1 1 0-2.857 1.429 1.429 0 0 1 0 2.857Zm2.143-6.797v2.244c0 .148-.12.268-.268.268H2.696a.268.268 0 0 1-.267-.268V2.696c0-.147.12-.267.267-.267h5.101c.071 0 .14.028.19.078l.077.078a.268.268 0 0 1 .079.19Z"
  })));
};

var _path$r;
function _extends$D() { _extends$D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$D.apply(this, arguments); }
var SvgAngleDown = function SvgAngleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 13 8"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M1.046 1.333 6.5 6.667l5.455-5.333"
  })));
};

var _path$q;
function _extends$C() { _extends$C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$C.apply(this, arguments); }
var SvgThLayout = function SvgThLayout(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    xmlns: "http://www.w3.org/2000/svg",
    "data-name": "Layer 1",
    viewBox: "0 0 512.23 512.44"
  }, props), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    d: "M300.03 512.1h188.6c13.1 0 23.6-12.2 23.6-27.2V303.5c0-15-10.5-27.2-23.6-27.2h-188.6c-13.1 0-23.6 12.2-23.6 27.2v181.4c0 15 10.6 27.2 23.6 27.2Zm0-276.3h188.6c13.1 0 23.6-12.2 23.6-27.2V27.2c0-15-10.5-27.2-23.6-27.2h-188.6c-13.1 0-23.6 12.2-23.6 27.2v181.4c0 15 10.6 27.2 23.6 27.2Zm-300-27.2v21.07V27.2c0-15 10.5-27.2 23.6-27.2h188.6c13.1 0 23.6 12.2 23.6 27.2v458c0 15-10.5 27.2-23.6 27.2H23.64c-13.1 0-23.6-12.2-23.6-27.2"
  })));
};

var _path$p;
function _extends$B() { _extends$B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$B.apply(this, arguments); }
var SvgThLarge = function SvgThLarge(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    xmlns: "http://www.w3.org/2000/svg",
    "data-name": "Layer 1",
    viewBox: "0 0 512.09 512.11"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    d: "M299.92 512.11h188.6c13.06 0 23.58-12.2 23.58-27.18V303.54c0-15-10.51-27.18-23.58-27.18h-188.6c-13.06 0-23.58 12.2-23.58 27.18v181.4c0 14.97 10.51 27.17 23.58 27.17Zm0-276.36h188.6c13.06 0 23.58-12.2 23.58-27.18V27.18c0-15-10.51-27.18-23.58-27.18h-188.6c-13.06 0-23.58 12.2-23.58 27.18v181.4c0 14.97 10.51 27.17 23.58 27.17Zm-276.34 0h188.6c13.06 0 23.58-12.2 23.58-27.18V27.18c0-15-10.51-27.18-23.58-27.18H23.58C10.51 0 0 12.2 0 27.18v181.4c0 14.97 10.51 27.17 23.58 27.17Zm0 276.36h188.6c13.06 0 23.58-12.2 23.58-27.18V303.54c0-15-10.51-27.18-23.58-27.18H23.58C10.51 276.36 0 288.56 0 303.54v181.4c0 14.97 10.51 27.17 23.58 27.17Z"
  })));
};

var _path$o;
function _extends$A() { _extends$A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$A.apply(this, arguments); }
var SvgWindowOpen = function SvgWindowOpen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 32 24"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    d: "M29 24H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3h26a3 3 0 0 1 3 3v18a3 3 0 0 1-3 3Zm1-5V5a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h22a3 3 0 0 0 3-3Z",
    "data-name": "Layer 2"
  })));
};

var _path$n;
function _extends$z() { _extends$z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$z.apply(this, arguments); }
var SvgWindowRight = function SvgWindowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    xmlns: "http://www.w3.org/2000/svg",
    "data-name": "Layer 1",
    viewBox: "0 0 32 24"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    d: "M29 24H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3h26a3 3 0 0 1 3 3v18a3 3 0 0 1-3 3ZM20 2H4.78A2.78 2.78 0 0 0 2 4.78v14.44A2.78 2.78 0 0 0 4.78 22H20V2Z"
  })));
};

var _path$m;
function _extends$y() { _extends$y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$y.apply(this, arguments); }
var SvgWindowBottom = function SvgWindowBottom(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 32 24"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    d: "M29 24H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3h26a3 3 0 0 1 3 3v18a3 3 0 0 1-3 3Zm1-10V5a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v9h28Z",
    "data-name": "Layer 2"
  })));
};

var _path$l;
function _extends$x() { _extends$x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$x.apply(this, arguments); }
var SvgWindowMaximize = function SvgWindowMaximize(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    xmlns: "http://www.w3.org/2000/svg",
    width: 14,
    height: 14,
    fill: "none"
  }, props), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.688.875H1.312C.589.875 0 1.463 0 2.188v9.624c0 .725.588 1.313 1.313 1.313h11.374c.725 0 1.313-.588 1.313-1.313V2.188c0-.725-.588-1.313-1.313-1.313ZM12.25 5.25H1.75V2.953a.33.33 0 0 1 .328-.328h9.844a.33.33 0 0 1 .328.328V5.25Z"
  })));
};

var _path$k;
function _extends$w() { _extends$w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$w.apply(this, arguments); }
var SvgWindowRestore = function SvgWindowRestore(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    xmlns: "http://www.w3.org/2000/svg",
    width: 14,
    height: 14,
    fill: "none"
  }, props), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14 1.313v7.875c0 .724-.588 1.312-1.313 1.312h-1.312V4.812a2.19 2.19 0 0 0-2.188-2.187H3.5V1.312C3.5.589 4.088 0 4.813 0h7.875C13.412 0 14 .588 14 1.313Zm-3.5 3.5v7.875c0 .724-.588 1.312-1.313 1.312H1.314A1.313 1.313 0 0 1 0 12.687V4.814C0 4.088.588 3.5 1.313 3.5h7.875c.724 0 1.312.588 1.312 1.313Zm-1.86.765a.33.33 0 0 0-.328-.328H2.079a.33.33 0 0 0-.328.328V7h6.89V5.578Z"
  })));
};

var _path$j;
function _extends$v() { _extends$v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$v.apply(this, arguments); }
var SvgBtc = function SvgBtc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 15.1 19.9"
    },
    viewBox: "0 0 15.1 19.9"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    d: "M15 8c.3-2.1-1.3-3.2-3.5-4l.7-2.8-1.7-.4-.6 2.7c-.5-.1-1-.2-1.4-.3L9.2.4 7.4 0l-.7 2.8c-.3 0-.7-.1-1.1-.2L3.2 2l-.4 1.8 1.3.3c.7.2.8.6.8 1L4 8.4l-1.1 4.5c-.1.2-.3.5-.8.4L.8 13 0 15l2.3.6 1.2.3-.7 2.9 1.7.4.7-2.8c.5.1.9.2 1.4.4l-.7 2.8 1.7.4.7-2.9c2.9.6 5.2.3 6.1-2.3.8-2.1 0-3.4-1.6-4.2 1.2-.3 2-1 2.2-2.6zm-3.9 5.6c-.5 2.1-4.1 1-5.3.7l.9-3.8c1.2.2 4.9.8 4.4 3.1zm.5-5.6c-.5 2-3.5 1-4.5.7L8 5.3c1 .2 4.1.7 3.6 2.7z"
  })));
};

var _defs$6, _use$5, _use2$2, _use3$2, _use4$1, _use5$1, _use6;
function _extends$u() { _extends$u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$u.apply(this, arguments); }
var SvgDigitalCurrencies = function SvgDigitalCurrencies(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 12,
    height: 12
  }, props), _defs$6 || (_defs$6 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "digital_currencies_svg__a",
    d: "M1263.28 75.8a1 1 0 0 1-.32.22v2.25c0 .27.34.47.58.47h.96c.25 0 .37-.2.37-.47v-4.11l-.01-.07z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "digital_currencies_svg__b",
    d: "m1273.72 68.82-2.07.1c-.1 0-.2.01-.27.15-.06.14.01.21.09.3l.62.66-2.11 2.27-.17.18-2.03 2.18-2.55-2.74a.43.43 0 0 0-.64 0l-2.46 2.65a.5.5 0 0 0 0 .68l.11.12a.43.43 0 0 0 .64 0l2.03-2.19 2.55 2.75a.43.43 0 0 0 .64 0l.1-.12 2.36-2.53.16-.18 2.12-2.26.61.65c.06.06.11.13.2.13l.1-.03c.12-.07.15-.19.15-.32l.1-2.14c0-.21-.08-.31-.28-.31z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "digital_currencies_svg__c",
    d: "M1266.17 71.18c.23.07.47.11.72.14.19.02.2.02.2.16v.19c0 .08.06.12.18.13h.4c.11 0 .17-.05.17-.12v-.26c0-.09.05-.13.17-.15.28-.06.52-.16.7-.31.52-.43.32-1.05-.4-1.33l-.72-.22a1.78 1.78 0 0 1-.38-.15c-.23-.12-.2-.33.08-.4.08-.03.16-.04.24-.04.31-.01.61.03.9.12.14.05.19.03.23-.07l.14-.32c.04-.07 0-.12-.1-.15-.19-.06-.37-.1-.57-.12-.25-.02-.25-.02-.25-.2 0-.25 0-.25-.37-.25h-.16c-.17 0-.2.02-.2.14v.16c0 .15 0 .15-.22.2-.53.14-.85.38-.89.78-.03.34.24.58.65.75.26.1.55.17.82.25l.3.12c.26.15.21.4-.1.49-.17.04-.34.06-.53.04a3.13 3.13 0 0 1-.8-.16c-.14-.05-.18-.04-.23.07l-.12.28c-.06.14-.04.17.14.23z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "digital_currencies_svg__d",
    d: "m1267.43 76.96-1.37-1.2a.38.38 0 0 0-.06.21v2.35c0 .23.21.42.47.42h1.06c.26 0 .47-.2.47-.42V77.3a1.1 1.1 0 0 1-.46-.24z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "digital_currencies_svg__e",
    d: "m1268.4 77-.1.1c-.1.08-.2.14-.3.19v1.04c0 .23.21.4.47.4h1.06c.26 0 .47-.18.47-.4v-2.29a.37.37 0 0 0-.14-.28z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "digital_currencies_svg__f",
    d: "m1271.29 73.65-.18.2-.11.12v4.25c0 .28.21.51.47.51h1.06c.26 0 .47-.23.47-.51V71.79z"
  }))), _use$5 || (_use$5 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#digital_currencies_svg__a",
    fill: "#fff",
    transform: "translate(-1262 -67)"
  })), _use2$2 || (_use2$2 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#digital_currencies_svg__b",
    fill: "#fff",
    transform: "translate(-1262 -67)"
  })), _use3$2 || (_use3$2 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#digital_currencies_svg__c",
    fill: "#fff",
    transform: "translate(-1262 -67)"
  })), _use4$1 || (_use4$1 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#digital_currencies_svg__d",
    fill: "#fff",
    transform: "translate(-1262 -67)"
  })), _use5$1 || (_use5$1 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#digital_currencies_svg__e",
    fill: "#fff",
    transform: "translate(-1262 -67)"
  })), _use6 || (_use6 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#digital_currencies_svg__f",
    fill: "#fff",
    transform: "translate(-1262 -67)"
  })));
};

var _defs$5, _g$1;
function _extends$t() { _extends$t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$t.apply(this, arguments); }
var SvgOptionCurrencies = function SvgOptionCurrencies(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 11,
    height: 12
  }, props), _defs$5 || (_defs$5 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "option_currencies_svg__a",
    d: "M1377.79 71.17v2.9h3.04c0-1.77-1.42-2.9-3.04-2.9zm5.1-1.14-.94-.83a.33.33 0 0 0-.44.01l-.26.26a.3.3 0 0 0 .02.42l.95.83c.13.11.33.1.45-.02l.25-.25a.29.29 0 0 0-.02-.42zm-4.42-.62v-.69h.56a.3.3 0 0 0 .31-.3v-.3a.3.3 0 0 0-.31-.29h-2.35a.3.3 0 0 0-.31.3v.3c0 .16.14.3.31.3h.5v.67a5.1 5.1 0 0 0-2.15.77c-.3.19-.37.58-.14.85.2.26.6.29.87.11.6-.38 1.31-.58 2.04-.58 2.26 0 4.04 1.92 3.65 4.11a3.6 3.6 0 0 1-3.09 2.85c-2.1.3-3.94-1.1-4.22-2.97h.43c.3 0 .48-.3.33-.55l-1.1-1.78a.4.4 0 0 0-.66 0l-1.09 1.78c-.15.24.04.55.34.55h.48a4.9 4.9 0 0 0 5.4 4.18 4.85 4.85 0 0 0 4.47-4.2 4.76 4.76 0 0 0-4.27-5.11z"
  }))), _g$1 || (_g$1 = /*#__PURE__*/React.createElement("g", {
    clipPath: "url(#option_currencies_svg__clip-97F0E02D-EA1F-450F-8BB5-3BB18E0C8B34)",
    transform: "translate(-1372 -67)"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#option_currencies_svg__a",
    fill: "#fff"
  }))));
};

var _path$i, _path2$4, _path3, _path4;
function _extends$s() { _extends$s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }
var SvgCoinStack = function SvgCoinStack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    "data-name": "Layer 1",
    viewBox: "0 0 13.87 13"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    d: "M4 7.35a7.82 7.82 0 0 0 1.1-.07V3.55A8.69 8.69 0 0 0 4 3.48c-2.18 0-4 .78-4 1.75v.38c0 .96 1.77 1.74 4 1.74ZM4 9.3a9.73 9.73 0 0 0 1.1-.06v-1.1a9.69 9.69 0 0 1-1.1.07c-1.9 0-3.5-.6-3.87-1.39a.83.83 0 0 0-.13.36v.38c0 1 1.77 1.74 4 1.74ZM5 10.18v-.09a9.69 9.69 0 0 1-1.1.07C2 10.16.45 9.56.08 8.77a.84.84 0 0 0-.08.36v.38c0 1 1.77 1.74 4 1.74a6.59 6.59 0 0 0 1.11-.07v-.62ZM9.53 0C7.14 0 5.2.88 5.2 1.95v.43c0 1.08 1.94 1.95 4.33 1.95s4.34-.87 4.34-1.95v-.43C13.87.88 11.93 0 9.53 0ZM9.53 6.65C7.44 6.65 5.7 6 5.29 5.2a.84.84 0 0 0-.09.37V6c0 1 1.94 1.8 4.33 1.8S13.87 7 13.87 6v-.4a.84.84 0 0 0-.1-.37c-.4.77-2.15 1.42-4.24 1.42Z"
  })), _path2$4 || (_path2$4 = /*#__PURE__*/React.createElement("path", {
    d: "M9.53 4.92c-2.09 0-3.83-.62-4.24-1.45a.84.84 0 0 0-.09.37v.4c0 1 1.94 1.83 4.33 1.83s4.34-.82 4.34-1.83v-.4a.84.84 0 0 0-.1-.37c-.4.83-2.15 1.45-4.24 1.45ZM9.53 8.38c-2.09 0-3.83-.62-4.24-1.45a.84.84 0 0 0-.09.37v.4c0 1 1.94 1.83 4.33 1.83s4.34-.82 4.34-1.83v-.4a.84.84 0 0 0-.1-.37c-.4.83-2.15 1.45-4.24 1.45Z"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    d: "M9.53 10.12c-2.09 0-3.83-.62-4.24-1.45A.84.84 0 0 0 5.2 9v.4c0 1 1.94 1.83 4.33 1.83s4.34-.82 4.34-1.83V9a.84.84 0 0 0-.1-.37c-.4.87-2.15 1.49-4.24 1.49Z"
  })), _path4 || (_path4 = /*#__PURE__*/React.createElement("path", {
    d: "M9.53 11.85c-2.09 0-3.83-.62-4.24-1.45a.84.84 0 0 0-.09.37v.4c0 1 1.94 1.83 4.33 1.83s4.34-.82 4.34-1.83v-.4a.84.84 0 0 0-.1-.37c-.4.83-2.15 1.45-4.24 1.45Z"
  })));
};

var _path$h;
function _extends$r() { _extends$r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }
var SvgTax = function SvgTax(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 384 512"
    },
    viewBox: "0 0 384 512"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    d: "M377 105 279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-153 31V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zM64 72c0-4.4 3.6-8 8-8h80c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8H72c-4.4 0-8-3.6-8-8V72zm0 80v-16c0-4.4 3.6-8 8-8h80c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8H72c-4.4 0-8-3.6-8-8zm4.8 100.1h66.3v28h-19.2v94.4H87.7v-94.4h-19v-28zm165.7 122.4h-28.9l-4.7-20.4h-29.3l-4.9 20.4h-28.3l32.5-122.4h31.2l32.4 122.4zm-38.8-42.4c-4.2-18.2-7.3-35.5-9.4-52h-.4c-2.2 16.9-5.3 34.3-9.3 52h19.1zm44.1-80h30.8c7.9 19.2 12.2 30.2 12.9 33h.4c.1-.9 1.3-4 3.5-9.4l9.4-23.7h30.8l-28.4 58.4 30.8 64h-30.8c-9.8-23-15-35.5-15.4-37.2h-.4c-1 3.9-2.4 7.8-4.1 11.6L268 374.4h-30.3l30.2-63.6-28.1-58.7z"
  })));
};

var _path$g, _path2$3;
function _extends$q() { _extends$q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }
var SvgTransaction = function SvgTransaction(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    "data-name": "Layer 1",
    viewBox: "0 0 13 12.99"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    d: "M5.47 7.5v.51a1.06 1.06 0 0 0 1.01 1 1 1 0 0 0 1-1c0-.49-.47-.76-1.23-1.16a2.32 2.32 0 0 1-1.56-2.08A1.78 1.78 0 0 1 6 3.07V1.94h1v1.14a1.79 1.79 0 0 1 1.3 1.7v.51h-1v-.5a.77.77 0 0 0-.8-.78.77.77 0 0 0-.78.75c0 .56.31.82 1 1.19a5.87 5.87 0 0 1 1.15.72 1.73 1.73 0 0 1 .63 1.34 2 2 0 0 1-1.5 2v1.08H6v-1.08a2.09 2.09 0 0 1-1.54-2v-.5Z"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/React.createElement("path", {
    d: "M1.9 1.91a6.44 6.44 0 0 1 8.29-.74A6.5 6.5 0 0 1 12 3.01V1.18h1v3.55H9.45v-1h1.75A5.49 5.49 0 0 0 1 6.52H0v-.15a6.46 6.46 0 0 1 1.9-4.46ZM0 8.29h3.55v1H1.8A5.47 5.47 0 0 0 12 6.5h1A6.48 6.48 0 0 1 1 9.96v1.88H0Z"
  })));
};

var _path$f;
function _extends$p() { _extends$p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }
var SvgArrowRight = function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 14 8"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M1 3.5a.5.5 0 0 0 0 1v-1Zm12.354.854a.5.5 0 0 0 0-.708L10.172.464a.5.5 0 0 0-.708.708L12.293 4 9.464 6.828a.5.5 0 1 0 .708.708l3.182-3.182ZM1 4.5h12v-1H1v1Z"
  })));
};

var _path$e;
function _extends$o() { _extends$o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }
var SvgDropArrowLeft = function SvgDropArrowLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    width: 27,
    height: 27,
    fill: "none"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M24.107 0H2.893A2.894 2.894 0 0 0 0 2.893v21.214A2.894 2.894 0 0 0 2.893 27h21.214A2.894 2.894 0 0 0 27 24.107V2.893A2.894 2.894 0 0 0 24.107 0ZM15.64 21.425l-7.413-7.413a.724.724 0 0 1 0-1.024l7.412-7.413a.723.723 0 0 1 1.236.512v14.826c0 .645-.777.97-1.236.512Z"
  })));
};

var _rect;
function _extends$n() { _extends$n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }
var SvgDropArrowCenter = function SvgDropArrowCenter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 27,
    height: 27,
    fill: "none"
  }, props), _rect || (_rect = /*#__PURE__*/React.createElement("rect", {
    width: 27,
    height: 27,
    fill: "currentColor",
    rx: 3
  })));
};

var _defs$4, _use$4;
function _extends$m() { _extends$m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }
var SvgWallet = function SvgWallet(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 18,
    height: 16
  }, props), _defs$4 || (_defs$4 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "wallet_svg__a",
    d: "M136.826 116.955a1.72 1.72 0 0 1 1.174 1.616v2.286c0 .745-.49 1.38-1.174 1.616v1.813c0 .945-.79 1.714-1.76 1.714h-12.131c-1.618 0-2.935-1.282-2.935-2.857v-6.857c0-1.576 1.317-2.857 2.935-2.857h1.489l3.795-3.285a.599.599 0 0 1 .78 0l3.795 3.285h2.271c.971 0 1.761.769 1.761 1.714zm-10.602-3.526h4.76l-2.43-1.977zm8.99 9.578c0-.033-1.03 0-1.03 0-1.605 0-3.543-1.168-3.543-3.262 0-2.095 1.938-3.426 3.544-3.426h1.029c0-.61-.362-1.322-1.617-1.322h-10.45c-.634 0-1.6.694-1.6 1.322v6.688c0 .653.917 1.486 1.486 1.486l10.564.128c1.735 0 1.617-1.143 1.617-1.614zm1.194-4.293c0-.315-.539-.714-.863-.714h-1.654c-.97 0-1.76.769-1.76 1.714 0 .946.79 1.715 1.76 1.715h1.654c.324 0 .863-.342.863-.657z"
  }))), _use$4 || (_use$4 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#wallet_svg__a",
    fill: "#b7bec5",
    transform: "translate(-120 -110)"
  })));
};

var _defs$3, _use$3, _use2$1, _use3$1, _use4, _use5;
function _extends$l() { _extends$l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }
var SvgExecution = function SvgExecution(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 18,
    height: 16
  }, props), _defs$3 || (_defs$3 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "execution_svg__a",
    d: "m123.067 264.862-1.307-.705 4.099-8.001 1.553.672 4.837 4.04 2.523-7.918 1.443.465-2.659 8.24-.832 1.544-5.956-4.973-.411.163z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "execution_svg__b",
    d: "M120 264.75a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "execution_svg__c",
    d: "M124.3 256.95a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "execution_svg__d",
    d: "M130 262.25a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "execution_svg__e",
    d: "M133.2 253.25a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0z"
  }))), _use$3 || (_use$3 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#execution_svg__a",
    fill: "#b7bec5",
    transform: "translate(-120 -251)"
  })), _use2$1 || (_use2$1 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#execution_svg__b",
    fill: "#b7bec5",
    transform: "translate(-120 -251)"
  })), _use3$1 || (_use3$1 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#execution_svg__c",
    fill: "#b7bec5",
    transform: "translate(-120 -251)"
  })), _use4 || (_use4 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#execution_svg__d",
    fill: "#b7bec5",
    transform: "translate(-120 -251)"
  })), _use5 || (_use5 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#execution_svg__e",
    fill: "#b7bec5",
    transform: "translate(-120 -251)"
  })));
};

var _defs$2, _use$2;
function _extends$k() { _extends$k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }
var SvgCog = function SvgCog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 16,
    height: 16
  }, props), _defs$2 || (_defs$2 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "cog_svg__a",
    d: "M135.746 447.741h-1.834a.618.618 0 0 0-.573.383l-.473 1.143a.619.619 0 0 0 .134.676l1.297 1.296-1.058 1.058-1.297-1.297a.616.616 0 0 0-.676-.134l-1.142.473a.619.619 0 0 0-.383.573v1.834h-1.496v-1.834a.618.618 0 0 0-.383-.573l-1.143-.473a.615.615 0 0 0-.676.134l-1.295 1.297-1.059-1.058 1.297-1.296a.618.618 0 0 0 .134-.676l-.473-1.143a.619.619 0 0 0-.573-.383h-1.833v-1.496h1.833c.252 0 .476-.15.573-.383l.473-1.142a.619.619 0 0 0-.134-.677l-1.297-1.295 1.058-1.058 1.296 1.295a.617.617 0 0 0 .676.135l1.143-.473a.619.619 0 0 0 .383-.573v-1.834h1.496v1.834c0 .252.15.477.383.573l1.142.473a.617.617 0 0 0 .676-.134l1.297-1.297 1.058 1.058-1.297 1.297a.619.619 0 0 0-.134.676l.473 1.143a.618.618 0 0 0 .573.382h1.834zm1.058-2.555a.625.625 0 0 0-.438-.182l-2.04.001-.156-.377 1.442-1.442a.62.62 0 0 0 0-.877l-1.935-1.935a.621.621 0 0 0-.438-.174.621.621 0 0 0-.439.174l-1.442 1.442-.377-.156v-2.04a.62.62 0 0 0-.62-.62h-2.736a.621.621 0 0 0-.62.62v2.04l-.377.156-1.443-1.442a.616.616 0 0 0-.876 0l-1.936 1.936a.625.625 0 0 0-.181.438c0 .163.066.323.181.439l1.442 1.44-.156.378h-2.039a.62.62 0 0 0-.62.62v2.737a.625.625 0 0 0 .62.62h2.04l.155.377-1.441 1.442a.62.62 0 0 0 0 .877l1.935 1.935c.11.11.27.174.439.174a.62.62 0 0 0 .438-.174l1.442-1.442.376.156v2.039c0 .342.28.62.621.62h2.743a.62.62 0 0 0 .614-.708v-1.952l.377-.155 1.442 1.441a.615.615 0 0 0 .876 0l1.936-1.935a.616.616 0 0 0 .181-.438.616.616 0 0 0-.181-.44l-1.443-1.44.157-.378h2.039a.62.62 0 0 0 .62-.62v-2.736a.617.617 0 0 0-.182-.44z"
  }))), _use$2 || (_use$2 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#cog_svg__a",
    fill: "#b7bec5",
    transform: "translate(-121 -439)"
  })));
};

var _defs$1, _use$1, _use2, _use3;
function _extends$j() { _extends$j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }
var SvgHamburger = function SvgHamburger(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 20,
    height: 16
  }, props), _defs$1 || (_defs$1 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "hamburger_svg__a",
    d: "M286 16a1 1 0 0 1 1-1h18a1 1 0 0 1 0 2h-18a1 1 0 0 1-1-1z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "hamburger_svg__b",
    d: "M286 23a1 1 0 0 1 1-1h18a1 1 0 0 1 0 2h-18a1 1 0 0 1-1-1z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "hamburger_svg__c",
    d: "M286 30a1 1 0 0 1 1-1h18a1 1 0 0 1 0 2h-18a1 1 0 0 1-1-1z"
  }))), _use$1 || (_use$1 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#hamburger_svg__a",
    fill: "#fff",
    transform: "translate(-286 -15)"
  })), _use2 || (_use2 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#hamburger_svg__b",
    fill: "#fff",
    transform: "translate(-286 -15)"
  })), _use3 || (_use3 = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#hamburger_svg__c",
    fill: "#fff",
    transform: "translate(-286 -15)"
  })));
};

var _defs, _use;
function _extends$i() { _extends$i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }
var SvgTimes = function SvgTimes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: 16,
    height: 16
  }, props), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "times_svg__a",
    d: "M553.708 15.293a.99.99 0 0 0-1.407 0l-6.301 6.3-6.301-6.3a.99.99 0 0 0-1.406 0 .99.99 0 0 0 0 1.406l6.301 6.3-6.301 6.3a.999.999 0 1 0 .7 1.7.97.97 0 0 0 .698-.293l6.302-6.301 6.301 6.3a.999.999 0 0 0 .7.295.958.958 0 0 0 .699-.294.99.99 0 0 0 0-1.406l-6.287-6.301 6.302-6.3a.991.991 0 0 0 0-1.406z"
  }))), _use || (_use = /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#times_svg__a",
    fill: "#fff",
    transform: "translate(-538 -15)"
  })));
};

var _path$d;
function _extends$h() { _extends$h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }
var SvgBlackAndWhite = function SvgBlackAndWhite(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 294 294"
    },
    viewBox: "0 0 294 294"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    d: "M294 15c0-8.284-6.716-15-15-15H15C6.716 0 0 6.716 0 15v264c0 8.284 6.716 15 15 15h264c8.284 0 15-6.716 15-15V15zM147 263H31V32h116v231z"
  })));
};

var _path$c;
function _extends$g() { _extends$g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }
var SvgTradingView = function SvgTradingView(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 33,
    height: 19
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    fill: "#3BB3E4",
    d: "M29.032 7.382a5.47 5.47 0 0 1 .963 2.872A4.502 4.502 0 0 1 28.5 19H6a5.98 5.98 0 0 1-4.222-1.737l9.546-7.556c.35.187.75.293 1.176.293a2.49 2.49 0 0 0 1.066-.238l4.55 3.981a2.5 2.5 0 1 0 4.711-.157l6.205-6.204zm-1.414-1.414-6.204 6.204A2.494 2.494 0 0 0 20.5 12a2.49 2.49 0 0 0-1.066.238l-4.55-3.981a2.5 2.5 0 1 0-4.801-.118L.608 15.638A6 6 0 0 1 6.061 7a8.001 8.001 0 0 1 15.625-1.227A5.474 5.474 0 0 1 24.5 5c1.157 0 2.231.358 3.118.968z"
  })));
};

var _path$b;
function _extends$f() { _extends$f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }
var SvgBriefcase = function SvgBriefcase(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 15 14"
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    d: "M9 0H6c-.832 0-1.5.656-1.5 1.474v1.473h-3c-.833 0-1.5.656-1.5 1.474v8.105c0 .391.158.766.44 1.042.28.277.662.432 1.06.432h12c.398 0 .78-.155 1.06-.432a1.46 1.46 0 0 0 .44-1.042V4.421c0-.39-.158-.766-.44-1.042a1.514 1.514 0 0 0-1.06-.432h-3V1.474c0-.391-.158-.766-.44-1.042A1.514 1.514 0 0 0 9 0Zm0 1.474v1.473H6V1.474h3Zm.375 5.748V8.39L12 6.444 9.375 4.5v1.167h-5.5v1.555h5.5Zm1.75 2.556h-5.5V8.61L3 10.556 5.625 12.5v-1.167h5.5V9.778Z",
    clipRule: "evenodd"
  })));
};

function _extends$e() { _extends$e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }
var SvgDefaultIcon = function SvgDefaultIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    "data-name": "Layer 1",
    viewBox: "0 0 32 32"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M25.4 15.93a11.25 11.25 0 0 0-.09-1.32 8.86 8.86 0 0 0-.36-1.6 9.18 9.18 0 0 0-.78-1.75 8.83 8.83 0 0 0-1.34-1.77 10 10 0 0 0-1.7-1.44 8.81 8.81 0 0 0-1.75-.9 9 9 0 0 0-1.72-.46A10.16 10.16 0 0 0 16 6.56a8.37 8.37 0 0 0-3.81.79 11.21 11.21 0 0 0-2.63 1.84A10 10 0 0 0 8.13 11a10.29 10.29 0 0 0-.9 1.78 8.08 8.08 0 0 0-.45 1.68 10.28 10.28 0 0 0-.12 1.5 10.8 10.8 0 0 0 .54 3.14 8.58 8.58 0 0 0 2 3.32 9.07 9.07 0 0 0 3.2 2.25 9.5 9.5 0 0 0 3.62.72 10.7 10.7 0 0 0 1.54-.12 8.9 8.9 0 0 0 1.7-.43 8.56 8.56 0 0 0 1.74-.89 9.15 9.15 0 0 0 1.72-1.47 9.75 9.75 0 0 0 2-3.07 9.36 9.36 0 0 0 .67-3.49ZM8.34 6.2a13.61 13.61 0 0 1 2.26-1.44 12.61 12.61 0 0 1 2.08-.82 10.78 10.78 0 0 1 1.82-.37 14.17 14.17 0 0 1 1.5-.1 14.81 14.81 0 0 1 1.49.09 11 11 0 0 1 1.87.37 13.35 13.35 0 0 1 2.12.82 13 13 0 0 1 2.27 1.45l6.1-6.2L32 2.19l-6.13 6.13a12.44 12.44 0 0 1 1.28 2.05 12.12 12.12 0 0 1 1.18 4 14.72 14.72 0 0 1 .1 1.66 13 13 0 0 1-.63 4 11.94 11.94 0 0 1-1.94 3.67L32 29.78 29.85 32l-6.1-6.2a14.22 14.22 0 0 1-1.31.91 10.42 10.42 0 0 1-1.7.84 13.92 13.92 0 0 1-2.14.63 12.43 12.43 0 0 1-2.61.25 14.33 14.33 0 0 1-1.81-.12 11.75 11.75 0 0 1-3.9-1.23 11.28 11.28 0 0 1-1.94-1.28L2.15 32 0 29.78l6.18-6.14a14.47 14.47 0 0 1-1.87-3.38A11.49 11.49 0 0 1 3.56 16a11.41 11.41 0 0 1 .76-4.25 15.11 15.11 0 0 1 1.86-3.43L0 2.19 2.15 0Z",
    style: {
      fill: "#686868"
    }
  }));
};

var _path$a;
function _extends$d() { _extends$d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }
var SvgChartArea = function SvgChartArea(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 32,
    height: 32
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    d: "M32 29.313v2.656H0V0h2.656v29.313H32zM5.344 2.656l9.719 12.188 6.281-5.469 7.969 9.281v8H5.344v-24z"
  })));
};

var _path$9;
function _extends$c() { _extends$c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }
var SvgChartBar = function SvgChartBar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 32,
    height: 32
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    d: "M32 29.344V32H0V0h2.656v29.344H32zM10.656 2.656h2.688v24h-2.688v-24zm2.688 18.688v-2.688h5.313v2.688h-5.313zM10.656 8v2.656H5.312V8h5.344zm16-5.344H24v24h2.656v-24zM24 16v-2.656h-5.344V16H24zm2.656-10.687v2.688H32V5.313h-5.344z"
  })));
};

var _path$8;
function _extends$b() { _extends$b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }
var SvgChartCandlestick = function SvgChartCandlestick(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 32,
    height: 32
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    d: "M32 29.344V32H0V0h2.656v29.344H32zM10.656 10.656h2.688v16h-2.688v-16zm-2.656 8h8v8H8v-8zm16 8h-2.656V0H24v26.656zM18.656 5.344h8v13.313h-8V5.344z"
  })));
};

var _path$7;
function _extends$a() { _extends$a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }
var SvgChartLine = function SvgChartLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 32,
    height: 32
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    d: "M32 29.344V32H0V0h2.656v29.313H32v.031zM5.344 2.688 16 13.344 21.344 8l8 10.688v5.344l-8-13.344L16 16.032 5.344 8.001V2.688z"
  })));
};

var _path$6;
function _extends$9() { _extends$9 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }
var SvgDrawingsZig = function SvgDrawingsZig(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: 32,
    height: 32
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    d: "m1.844 22.406 4.031-3.969q.5.469 1.078.719t1.234.25 1.234-.234 1.047-.672l1.688 2q-.063.313-.125.641t-.063.641q0 1.875 1.109 3.203t2.672 1.328q1.594 0 2.703-1.313t1.109-3.219q0-.313-.063-.641t-.125-.641l6.656-6.531q.469.375 1.016.594t1.141.219q1.594 0 2.703-1.344t1.109-3.219-1.109-3.203-2.703-1.328q-1.563 0-2.672 1.328t-1.109 3.203q0 .469.078.922t.234.859l-6.5 6.344q-.5-.5-1.125-.797t-1.344-.297q-.688 0-1.297.281t-1.109.781l-1.594-1.906q.125-.344.172-.734t.047-.797q0-1.875-1.094-3.203t-2.688-1.328q-1.563 0-2.672 1.328t-1.109 3.203q0 .406.063.781t.188.719L.499 20.468q-.344.313-.375.844t.25.906q.25.406.688.453t.781-.266zm26.344-14.25q.719 0 1.219.594t.5 1.469q0 .813-.5 1.422t-1.219.609q-.688 0-1.188-.594t-.5-1.438.5-1.453 1.188-.609zM15.75 19.75q.719 0 1.203.578t.484 1.422-.484 1.438-1.203.594q-.688 0-1.188-.594t-.5-1.438.5-1.422 1.188-.578zm-7.562-6.906q.719 0 1.203.594t.484 1.438-.5 1.438-1.188.594-1.188-.594-.5-1.438.5-1.438 1.188-.594z"
  })));
};

function _extends$8() { _extends$8 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }
var SvgCoinbase = function SvgCoinbase(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 100 100.1"
    },
    viewBox: "0 0 100 100.1"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M64.5 81.3c5.1 0 10.2-1.1 14.8-2.8L89 93.3c-8.5 4.5-17.6 6.8-27.3 6.8-29 0-50.6-19.3-50.6-50C11.6 19.3 33.8 0 62.2 0c10.2 0 17.6 2.3 25.6 6.3l-9.1 15.4c-4.5-1.7-9.7-2.8-14.8-2.8-17.1 0-30.7 10.8-30.7 31.3 0 19.2 13.1 31.1 31.3 31.1z",
    style: {
      fill: "#0667d0"
    }
  }));
};

var _path$5;
function _extends$7() { _extends$7 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }
var SvgGemini = function SvgGemini(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 324 324"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    fill: "#00dcfa",
    d: "M211.74 0c-57 0-105.39 43.84-111.49 100.25C43.84 106.35 0 154.76 0 211.74A112.34 112.34 0 0 0 112.26 324c57 0 105.58-43.84 111.49-100.25C280.16 217.65 324 169.24 324 112.26A112.34 112.34 0 0 0 211.74 0zm86 124.84A87.35 87.35 0 0 1 224.51 198v-73.16zM26.3 199.16a87.35 87.35 0 0 1 73.19-73.37V199H26.3zm171.91 25.35a86.9 86.9 0 0 1-171.91 0zm.95-99.67V199h-74.32v-74.16zm98.54-25.35H125.79a86.9 86.9 0 0 1 171.91 0z"
  })));
};

function _extends$6() { _extends$6 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }
var SvgKraken = function SvgKraken(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 100 100.1"
    },
    viewBox: "0 0 100 100.1"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M50 9.8c-27.7 0-50 22.3-50 50V83c0 3.9 3.2 7.1 7.1 7.1s7.1-3.2 7.1-7.1V61.6c0-3.9 3.2-7.1 7.1-7.1s7.1 3.2 7.1 7.1V83c0 3.9 3.2 7.1 7.1 7.1s7.1-3.2 7.1-7.1V61.6c0-3.9 3.2-7.1 7.1-7.1s7.1 3.2 7.1 7.1V83c0 3.9 3.2 7.1 7.1 7.1S71 86.9 71 83V61.6c0-3.9 3.2-7.1 7.1-7.1s7.1 3.2 7.1 7.1V83c0 3.9 3.2 7.1 7.1 7.1s7.1-3.2 7.1-7.1V59.8c.6-27.6-21.7-50-49.4-50z",
    style: {
      fill: "#4f6e89"
    }
  }));
};

var _g, _path$4, _path2$2;
function _extends$5() { _extends$5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }
var SvgBitstamp = function SvgBitstamp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 446.4 446.4"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    fill: "#149f49",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M249.84 197.5c-7.78-1.23-15.77-.8-23.62-.87-11.66 0-23.32.15-34.92-.07q-.21 31.65 0 63.29c14-.15 27.94-.07 41.91-.07 7.41 0 15 .28 22.1-2.38a30.07 30.07 0 0 0 19.23-21.82c1.65-8.35.79-17.49-4-24.69-4.54-7.2-12.49-11.74-20.7-13.39z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M223.2 0C99.93 0 0 99.93 0 223.2s99.93 223.2 223.2 223.2 223.2-99.93 223.2-223.2S346.47 0 223.2 0zm-93 262.3c11.88.21 23.25-7.35 28.15-18.08a33.23 33.23 0 0 0 2.88-16.05c.5-10.66-4.68-21.53-13.75-27.36-5-3.6-11.16-5-17.21-5.4-.07-8.43 0-16.78-.07-25.13 8.71.36 17.2-3.67 23-10.08a31.86 31.86 0 0 0 8-23c-1-10.22-7-20.09-16.27-24.77-4.47-2.59-9.58-3.53-14.69-3.74-.15-9.22 0-18.36-.07-27.51 39.67-.07 79.34-.14 119.08 0 15.77 1 31.76 6.56 43.49 17.43a50.33 50.33 0 0 1 15.12 27.79c1.73 12.74.58 26.64-7.13 37.37-4.89 7.63-12.38 13.17-20.37 17.35 12.38 4.75 23.47 13.46 29.66 25.34 8.5 16.28 8.28 36.87-.86 52.78-6.27 11.16-16.78 19.58-28.51 24.48s-24.77 6.62-37.44 6.48h-113c-.03-9.33.04-18.62-.03-27.9zm186 102.88c-25.27.29-50.61.08-76 .15-36.65-.07-73.29.14-109.94-.15-.22-9.93-.22-19.94 0-29.95 38.3-.29 76.61 0 115-.07 23.62.07 47.31-.22 70.92.07.19 10.01.19 20.02-.03 29.95z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M229.18 169.2c6.26-.14 12.6.58 18.64-1.37 9.94-2.81 18.87-11.09 20.45-21.6 1.44-8.64-.21-18.21-6.26-24.84-6.41-7.13-16.35-10.58-25.78-10.22h-45.07c.07 19.37.07 38.66 0 58z"
  }))), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    d: "M243.22 290.16c12.67.14 25.7-1.44 37.44-6.48s22.24-13.32 28.51-24.48c9.14-15.91 9.36-36.5.86-52.78-6.19-11.88-17.28-20.59-29.66-25.34 8-4.18 15.48-9.72 20.37-17.35 7.71-10.73 8.86-24.63 7.13-37.37a50.33 50.33 0 0 0-15.12-27.79C281 87.7 265 82.15 249.26 81.14c-39.74-.14-79.41-.07-119.08 0 .07 9.15-.08 18.29.07 27.51 5.11.21 10.22 1.15 14.69 3.74 9.28 4.68 15.26 14.55 16.27 24.77a31.86 31.86 0 0 1-8 23c-5.84 6.41-14.33 10.44-23 10.08.07 8.35 0 16.7.07 25.13 6.05.43 12.24 1.8 17.21 5.4 9.07 5.83 14.25 16.7 13.75 27.36a33.23 33.23 0 0 1-2.88 16.05c-4.9 10.73-16.27 18.29-28.15 18.08.07 9.28 0 18.57 0 27.86zm31.32-54.58a30.07 30.07 0 0 1-19.23 21.82c-7.05 2.66-14.69 2.38-22.1 2.38-14 0-27.94-.08-41.91.07q-.21-31.65 0-63.29c11.6.22 23.26.07 34.92.07 7.85.07 15.84-.36 23.62.87 8.21 1.65 16.13 6.19 20.66 13.39 4.83 7.2 5.69 16.34 4.04 24.69zm-83.38-124.41h45.07c9.43-.36 19.37 3.09 25.78 10.22 6.05 6.63 7.7 16.2 6.26 24.84-1.58 10.51-10.51 18.79-20.45 21.6-6 1.95-12.38 1.23-18.64 1.37h-38c.05-19.37.05-38.66-.02-58.03z"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/React.createElement("path", {
    fill: "#73c692",
    d: "M245.23 335.16c-38.37.07-76.68-.22-115 .07-.22 10-.22 20 0 29.95 36.65.29 73.29.08 109.94.15 25.35-.07 50.69.14 76-.15.22-9.93.22-19.94 0-29.95-23.63-.29-47.32 0-70.94-.07z"
  })));
};

var _path$3;
function _extends$4() { _extends$4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }
var SvgBittrex = function SvgBittrex(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 75 85.7"
    },
    viewBox: "0 0 75 85.7"
  }, props), /*#__PURE__*/React.createElement("g", {
    style: {
      opacity: 0.1
    }
  }, _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    d: "M0 73.7h17.5v12H0zM25 65h17.5v20.7H25zM49.5 48.9H67v36.8H49.5z"
  }))), /*#__PURE__*/React.createElement("linearGradient", {
    id: "bittrex_svg__a",
    x1: 39.596,
    x2: 39.596,
    y1: 66.753,
    y2: 0,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: 0,
    style: {
      stopColor: "#222b8b"
    }
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 1,
    style: {
      stopColor: "#32a6dc"
    }
  })), /*#__PURE__*/React.createElement("path", {
    d: "M12.4 66.8V0h33.2c13.1 0 19.7 2.7 19.7 17.4 0 7.7-1.4 12.8-9.5 14.8v.4c9.3 1.1 11.1 7.2 11.1 15.7 0 14.8-6.1 18.6-20.1 18.6H12.4zm12.8-38.9h18.4c7.2 0 8.8-2.2 8.8-9.3 0-7.1-2.8-7.7-9.2-7.7h-18v17zm0 27.9h17.5c7.7 0 11.3-.5 11.3-8.7 0-7.3-1.6-9.4-9.2-9.4H25.2v18.1z",
    style: {
      fill: "url(#bittrex_svg__a)"
    }
  }));
};

var _style$1, _path$2;
function _extends$3() { _extends$3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }
var SvgPoloniex = function SvgPoloniex(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 468.3 468.2"
    },
    viewBox: "0 0 468.3 468.2"
  }, props), _style$1 || (_style$1 = /*#__PURE__*/React.createElement("style", null, ".poloniex_svg__st0{fill:#29343a}")), /*#__PURE__*/React.createElement("g", {
    id: "poloniex_svg__Layer_4"
  }, _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    d: "M335.8 189.6 461.2 0H324.3l-59 86.4zM335.5 277.3l-70.2 98 66.4 92.7h136.6z",
    className: "poloniex_svg__st0"
  })), /*#__PURE__*/React.createElement("path", {
    d: "m304.6 234.2-168.2 234H0l163.1-233.6L7.4.1h137l160.2 234.1",
    style: {
      fill: "#096166"
    }
  })));
};

var _style, _path$1, _path2$1;
function _extends$2() { _extends$2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
var SvgDltxmm = function SvgDltxmm(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    id: "dltxmm_svg__Layer_1",
    x: 0,
    y: 0,
    style: {
      enableBackground: "new 0 0 74.9 74.9"
    },
    viewBox: "0 0 74.9 74.9"
  }, props), _style || (_style = /*#__PURE__*/React.createElement("style", null, ".dltxmm_svg__st1{fill:#1f55a4}")), /*#__PURE__*/React.createElement("linearGradient", {
    id: "dltxmm_svg__SVGID_1_",
    x1: -206.296,
    x2: -164.753,
    y1: -465.157,
    y2: -465.157,
    gradientTransform: "matrix(.9604 -.0396 .0396 -.9604 212.555 -427.217)",
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: 0,
    style: {
      stopColor: "#3d63ae"
    }
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 1,
    style: {
      stopColor: "#2fc4f3"
    }
  })), /*#__PURE__*/React.createElement("path", {
    d: "M15.4 52.9 0 37.4 37.4 0v30.9z",
    style: {
      fill: "url(#dltxmm_svg__SVGID_1_)"
    }
  }), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    d: "M27.3 41v23.8L15.4 52.9z",
    className: "dltxmm_svg__st1"
  })), /*#__PURE__*/React.createElement("linearGradient", {
    id: "dltxmm_svg__SVGID_2_",
    x1: -341.891,
    x2: -300.356,
    y1: -311.514,
    y2: -311.514,
    gradientTransform: "matrix(-.9604 .0396 -.0396 .9604 -261.825 359.904)",
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: 0,
    style: {
      stopColor: "#3d63ae"
    }
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 1,
    style: {
      stopColor: "#2fc4f3"
    }
  })), /*#__PURE__*/React.createElement("path", {
    d: "m59.4 22 15.5 15.4-37.5 37.5V44z",
    style: {
      fill: "url(#dltxmm_svg__SVGID_2_)"
    }
  }), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    d: "M47.5 33.9V10.1L59.4 22z",
    className: "dltxmm_svg__st1"
  })));
};

var _path, _path2;
function _extends$1() { _extends$1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }
var SvgDeltix = function SvgDeltix(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    width: 922,
    height: 922
  }, props), /*#__PURE__*/React.createElement("linearGradient", {
    id: "deltix_svg__a",
    x1: -571.802,
    x2: 959.926,
    y1: 461,
    y2: 461,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: 0,
    style: {
      stopColor: "#213d77"
    }
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 0.244,
    style: {
      stopColor: "#368aca"
    }
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 0.575,
    style: {
      stopColor: "#388ccb"
    }
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 1,
    style: {
      stopColor: "#2c3584"
    }
  })), _path || (_path = /*#__PURE__*/React.createElement("path", {
    fill: "url(#deltix_svg__a)",
    d: "M922 461.005 461.302 922 0 460.991 461.302 0z"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    fill: "#FFF",
    d: "M330.385 130.824 922 461.005 330.385 791.236z"
  })));
};

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var SvgBitfinex = function SvgBitfinex(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      enableBackground: "new 0 0 100 100.1"
    },
    viewBox: "0 0 100 100.1"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M3.7 85.5c4.6 4.9 29.9 28.2 69.7 1.2 28.7-22.3 28.1-70.9 25.3-85.6 0 0-.2 0-.5-.2C96.9 4 63.9 76.4 3.7 85.5zM98.1 1.1C93.3.4 58.8-2.5 24.8 19.5 2.2 34.1-.9 55.5.1 69.6c52.8-5.8 96.1-66 98-68.5z",
    style: {
      fill: "#a3bf00"
    }
  }));
};

export { AppContainer, BlueButton, Button, Checkbox, CloseButton, ContentBlock, EmotionCacheProvider, FavoriteButton, Flex, Flex1Horizontal, Flex1Vertical, FlexColumns, FlexRows, GreenButton, GreenProgress, GreenText, HeaderButton, SvgAngleDown as IconAngleDown, SvgArrowRight as IconArrowRight, SvgBitstamp as IconBitstamp, SvgBittrex as IconBittrex, SvgBlackAndWhite as IconBlackAndWhite, SvgBriefcase as IconBriefcase, SvgBtc as IconBtc, SvgChartArea as IconChartArea, SvgChartBar as IconChartBar, SvgChartCandlestick as IconChartCandlestick, SvgChartLine as IconChartLine, SvgChartTv as IconChartTv, SvgCog as IconCog, SvgCoinStack as IconCoinStack, SvgCoinbase as IconCoinbase, SvgCopy as IconCopy, SvgDefaultIcon as IconDefault, SvgDigitalCurrencies as IconDigitalCurrencies, SvgDrawingsZig as IconDrawingsZig, SvgDropArrowCenter as IconDropArrowCenter, SvgDropArrowLeft as IconDropArrowLeft, SvgExecution as IconExecution, SvgExternal as IconExternal, SvgFloppyDisk as IconFloppyDisk, SvgGemini as IconGemini, SvgHamburger as IconHamburger, SvgKraken as IconKraken, SvgOptionCurrencies as IconOptionCurrencies, SvgTax as IconTax, SvgThLarge as IconThLarge, SvgThLayout as IconThLayout, SvgTimes as IconTimes, SvgTradingView as IconTradingView, SvgTransaction as IconTransaction, SvgWallet as IconWallet, SvgWindowBottom as IconWindowBottom, SvgWindowMaximize as IconWindowMaximize, SvgWindowOpen as IconWindowOpen, SvgWindowRestore as IconWindowRestore, SvgWindowRight as IconWindowRight, SvgBitfinex as IconpBitfinex, SvgDeltix as IconpDeltix, SvgDltxmm as IconpDltxmm, SvgPoloniex as IconpPoloniex, Label$1 as Label, OnClickStopPropagation, OuterCloseButton, Overlay, OverlayWrapper, PasswordInput, Progress, RadioButton, RedButton, RedProgress, RedText, SecurityFilterButton, StopPropagation, TextInput, Textarea, TransparentButton, TransparentOverlay, VerificationCodeInput, WhiteButton, YellowButton, baseTheme, borderBottomLeftRadius, borderBottomRightRadius, borderRadius, borderTopLeftRadius, borderTopRightRadius, bottom, breakpoints, buttonStylesMixin, colors2, components, fontFamily, fontSize, fontWeight, gap, getTheme, gridColumnGap, gridGap, gridRowGap, height, hover, left, lineHeight, margin, marginBottom, marginLeft, marginRight, marginTop, maxHeight, maxWidth, minHeight, minWidth, padding, paddingBottom, paddingLeft, paddingRight, paddingTop, rgba, right, staticValues, top, useCssClassName, useEmotionCache, width, withCssClassName };
